import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { topics } from "@/mocks/topics";

const mainNav = [
  { label: "首页", to: "/", end: true },
  { label: "电影", to: "/category/movie", end: false },
  { label: "电视剧", to: "/category/tv", end: false },
  { label: "专题", to: "/topic/marriage", end: false, hasDropdown: true },
  { label: "关于", to: "/about", end: false },
  { label: "联系", to: "/contact", end: false },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [topicOpen, setTopicOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const topicRef = useRef<HTMLDivElement>(null);

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setTopicOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (topicRef.current && !topicRef.current.contains(e.target as Node)) {
        setTopicOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const transparent = isHome && !scrolled && !mobileOpen;

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setSearchOpen(false);
    setKeyword("");
  };

  const shell = transparent
    ? "bg-transparent"
    : "bg-background-50/95 backdrop-blur border-b border-background-200";
  const textColor = transparent ? "text-background-50" : "text-foreground-800";

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${shell}`}>
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <span
              className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg ${
                transparent ? "bg-background-50/15 text-background-50" : "bg-primary-500 text-background-50"
              }`}
            >
              <i className="ri-film-line text-xl"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className={`font-heading font-extrabold text-lg md:text-xl tracking-tight ${textColor}`}>
                欧美伦理
              </span>
              <span className={`text-[10px] md:text-[11px] tracking-[0.2em] ${transparent ? "text-background-50/70" : "text-foreground-500"}`}>
                ETHICAL CINEMA
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {mainNav.map((item) =>
              item.hasDropdown ? (
                <div key={item.to} className="relative" ref={topicRef}>
                  <button
                    onClick={() => setTopicOpen((v) => !v)}
                    className={`flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                      transparent ? "text-background-50/90 hover:bg-background-50/10" : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    {item.label}
                    <i className={`ri-arrow-down-s-line transition-transform ${topicOpen ? "rotate-180" : ""}`}></i>
                  </button>
                  {topicOpen && (
                    <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 border border-background-200 rounded-lg p-2 animate-slide-down">
                      {topics.map((t) => (
                        <Link
                          key={t.id}
                          to={`/topic/${t.id}`}
                          className="flex flex-col gap-0.5 px-3 py-2.5 rounded-md hover:bg-background-100 transition-colors"
                        >
                          <span className="text-sm font-medium text-foreground-900">{t.title}</span>
                          <span className="text-xs text-foreground-500">{t.subtitle}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? transparent
                          ? "text-background-50 bg-background-50/15"
                          : "text-primary-600 bg-primary-50"
                        : transparent
                          ? "text-background-50/85 hover:bg-background-50/10"
                          : "text-foreground-700 hover:bg-background-100"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center">
              {searchOpen ? (
                <form onSubmit={submitSearch} className="flex items-center animate-fade-in">
                  <input
                    ref={searchInputRef}
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="搜索影视名称或标签"
                    className={`w-52 xl:w-64 text-sm px-3 py-2 rounded-md border outline-none transition-colors ${
                      transparent
                        ? "bg-background-50/95 border-transparent text-foreground-900 placeholder:text-foreground-400"
                        : "bg-background-100 border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400"
                    }`}
                  />
                  <button
                    type="submit"
                    aria-label="提交搜索"
                    className={`w-9 h-9 flex items-center justify-center rounded-md cursor-pointer ${
                      transparent ? "text-background-50" : "text-primary-600 hover:bg-primary-50"
                    }`}
                  >
                    <i className="ri-search-line"></i>
                  </button>
                </form>
              ) : (
                <button
                  onClick={() => setSearchOpen(true)}
                  aria-label="展开搜索"
                  className={`w-10 h-10 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                    transparent ? "text-background-50 hover:bg-background-50/10" : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  <i className="ri-search-line text-lg"></i>
                </button>
              )}
            </div>

            <Link
              to="/category/movie"
              className="hidden lg:inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap bg-accent-500 text-foreground-950 hover:bg-accent-600 transition-colors"
            >
              <i className="ri-fire-line"></i>
              开始探索
            </Link>

            <button
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="打开菜单"
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                transparent ? "text-background-50 hover:bg-background-50/10" : "text-foreground-800 hover:bg-background-100"
              }`}
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 animate-slide-down">
          <div className="px-4 py-4">
            <form onSubmit={submitSearch} className="flex items-center gap-2 mb-4">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索影视名称或标签"
                  className="w-full text-sm pl-9 pr-3 py-2.5 rounded-md bg-background-100 border border-background-200 outline-none text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>
            <nav className="flex flex-col">
              {mainNav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-md text-base font-medium transition-colors ${
                      isActive ? "text-primary-600 bg-primary-50" : "text-foreground-800 hover:bg-background-100"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="mt-4 pt-4 border-t border-background-200">
              <p className="text-xs font-semibold text-foreground-500 mb-2 px-3">精选专题</p>
              <div className="flex flex-col">
                {topics.map((t) => (
                  <Link
                    key={t.id}
                    to={`/topic/${t.id}`}
                    className="px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                  >
                    {t.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}